import { useEffect, useRef, useState } from 'react';
import ChartistGraph from 'react-chartist';
import { computePieChartSize, getDashboardHeightScale } from '../dashboardLayout';

const ALLOCATION_COLORS = ['#0948B3', '#1B998B', '#F8BD7A'];
const MIN_SLICE_PERCENT = 2;
const CHART_SIZE_FACTOR = 0.75;

function AssetAllocationCard(props) {
  const cardBodyRef = useRef(null);
  const [chartSize, setChartSize] = useState(130);
  const { labels = [], series = [], totalValue = 0 } = props.chartData || {};
  const minValue = (totalValue * MIN_SLICE_PERCENT) / 100;
  const displaySeries = series.map((value) => Math.max(value, minValue));
  const donutWidth = Math.max(20, Math.round(chartSize * 0.27));

  useEffect(() => {
    const element = cardBodyRef.current;
    if (!element) {
      return undefined;
    }

    const updateSize = () => {
      const { clientWidth, clientHeight } = element;
      if (clientWidth <= 0) {
        return;
      }

      const size = computePieChartSize({
        clientWidth,
        clientHeight,
        legendItemCount: labels.length,
        scale: getDashboardHeightScale(element),
        legendPosition: 'bottom'
      });

      if (size) {
        const scaledSize = Math.floor(size * CHART_SIZE_FACTOR);
        setChartSize((current) => (Math.abs(current - scaledSize) <= 1 ? current : scaledSize));
      }
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, [labels.length]);

  const options = {
    height: `${chartSize}px`,
    width: `${chartSize}px`,
    showLabel: false,
    donut: true,
    donutWidth,
    donutSolid: true
  };

  return (
    <div className="col-12 col-xl-3">
      <div className="card border-light shadow-sm asset-allocation-card-wrapper">
        <div className="card-header d-flex flex-row align-items-center">
          <div className="h4">Asset Allocation</div>
        </div>
        <div className="card-body asset-allocation-card" ref={cardBodyRef}>
          <div className="asset-allocation-chart">
            <div
              className="asset-allocation-chart-pie"
              style={{ width: chartSize, height: chartSize }}
            >
              <ChartistGraph data={{ labels, series: displaySeries }} type="Pie" options={options} />
            </div>
            <ul className="asset-allocation-legend list-unstyled mb-0">
              {labels.map((label, idx) => (
                <li key={label} className="asset-allocation-legend-item">
                  <span
                    className="asset-allocation-legend-swatch"
                    style={{ backgroundColor: ALLOCATION_COLORS[idx % ALLOCATION_COLORS.length] }}
                  />
                  <span className="asset-allocation-legend-label">
                    {label}
                    {totalValue > 0 && (
                      <span className="asset-allocation-legend-percent">
                        {((series[idx] / totalValue) * 100).toFixed(1)}%
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssetAllocationCard;
